import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { addYears, format, parseISO } from 'date-fns';
import incomeStatementAPI from '../api/incomeStatementAPI';
import generalLedgerAPI from '../api/generalLedgerAPI';
import useCompanyContext from '../hooks/useCompanyContext';
import ReportToolbar from '../shared/ReportToolbar';
import { computePeriodRange } from '../shared/PeriodSelect';
import StatementHeader from '../features/reports/components/StatementHeader';
import notify from '../lib/notify';

const shiftYear = (isoDate, years) => format(addYears(parseISO(isoDate), years), 'yyyy-MM-dd');

// ── Money formatting: parentheses for negatives, ₱ on the first amount shown
// and on every total/subtotal line only — never repeated on every detail row.
const formatAbs = (amount) =>
  new Intl.NumberFormat('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Math.abs(Number(amount) || 0));

const formatMoney = (amount, { showCurrency = false } = {}) => {
  const value = Number(amount) || 0;
  const withCurrency = showCurrency ? `₱${formatAbs(value)}` : formatAbs(value);
  return value < 0 ? `(${withCurrency})` : withCurrency;
};

const fmtTxnDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-PH', { month: '2-digit', day: '2-digit', year: 'numeric' }) : '—';

const IncomeStatementComparative = () => {
  const { accountingClientId: contextClientId } = useCompanyContext();
  const accountingClientId = contextClientId || null;
  const isBilling = localStorage.getItem('tenantType') === 'billing';

  const [accountingClientName, setAccountingClientName] = useState(
    () => localStorage.getItem('accountingClientName') || ''
  );
  useEffect(() => {
    setAccountingClientName(localStorage.getItem('accountingClientName') || '');
  }, [accountingClientId]);

  const [fiscalYear, setFiscalYear] = useState(() => new Date().getFullYear());
  useEffect(() => {
    const applyFY = () => {
      const key = accountingClientId ? `activeFiscalYear:${accountingClientId}` : null;
      const fy = key ? localStorage.getItem(key) : null;
      if (fy) setFiscalYear(parseInt(fy, 10));
    };
    applyFY();
    window.addEventListener('fiscalYear:changed', applyFY);
    return () => window.removeEventListener('fiscalYear:changed', applyFY);
  }, [accountingClientId]);

  const [period, setPeriod] = useState(() => ({ preset: 'YTD', ...computePeriodRange('YTD') }));
  const [segment, setSegment] = useState('ALL');
  const [comparative, setComparative] = useState(false);

  const queryKey = ['income-statement', accountingClientId, segment, period.startDate, period.endDate, comparative];

  const { data, isFetching, isError, error, refetch } = useQuery({
    queryKey,
    enabled: !!accountingClientId,
    queryFn: async () => {
      if (comparative) {
        const res = await incomeStatementAPI.getComparative({
          startDate: period.startDate,
          endDate: period.endDate,
          compareStartDate: shiftYear(period.startDate, -1),
          compareEndDate: shiftYear(period.endDate, -1),
          accountingClientId,
          segment,
        });
        if (!res.success) throw new Error(res.message || 'Failed to fetch comparative income statement');
        return res.data; // { current, prior }
      }
      const res = await incomeStatementAPI.get({
        startDate: period.startDate,
        endDate: period.endDate,
        accountingClientId,
        segment,
      });
      if (!res.success) throw new Error(res.message || 'Failed to fetch income statement');
      return res.data;
    },
  });

  const current = comparative ? data?.current : data;
  const prior = comparative ? data?.prior : null;

  // ── Breakdown (drill-down per account into individual GL transactions) —
  // single-period view only, matching the original behavior.
  const [expandedAccounts, setExpandedAccounts] = useState(() => new Set());
  const [breakdownData, setBreakdownData] = useState({});
  const [loadingBreakdown, setLoadingBreakdown] = useState(() => new Set());

  useEffect(() => {
    setBreakdownData({});
    setExpandedAccounts(new Set());
  }, [period.startDate, period.endDate, accountingClientId, segment, comparative]);

  const toggleBreakdown = async (accountId) => {
    const id = String(accountId);
    if (expandedAccounts.has(id)) {
      setExpandedAccounts((prev) => { const s = new Set(prev); s.delete(id); return s; });
      return;
    }
    setExpandedAccounts((prev) => new Set(prev).add(id));
    if (breakdownData[id]) return;
    setLoadingBreakdown((prev) => new Set(prev).add(id));
    try {
      const res = await generalLedgerAPI.getAll({
        account: id,
        startDate: period.startDate,
        endDate: period.endDate,
        accountingClientId,
        // The API applies this only to Karyu during the staged rollout.
        journalEntryStatus: 'posted',
        limit: 1000,
      });
      setBreakdownData((prev) => ({ ...prev, [id]: res.success ? res.data : [] }));
    } catch (_) {
      setBreakdownData((prev) => ({ ...prev, [id]: [] }));
    } finally {
      setLoadingBreakdown((prev) => { const s = new Set(prev); s.delete(id); return s; });
    }
  };

  // ── Export: CSV (client-side, mirrors the PFRS section rows) ──────────────
  const exportCSV = () => {
    if (!current) return;
    const fmtCSV = (n) => parseFloat((Number(n) || 0).toFixed(2));
    const rows = [];

    if (comparative && prior) {
      rows.push(['Section', 'Account Number', 'Account Name', `Current (${period.startDate} to ${period.endDate})`, `Prior (${shiftYear(period.startDate, -1)} to ${shiftYear(period.endDate, -1)})`]);
      const section = (label, curLines, priorLines, curTotal, priorTotal) => {
        const byKey = new Map();
        curLines.forEach((l) => byKey.set(l.accountNumber, { ...l, current: l.amount, prior: 0 }));
        priorLines.forEach((l) => {
          const existing = byKey.get(l.accountNumber);
          if (existing) existing.prior = l.amount;
          else byKey.set(l.accountNumber, { ...l, current: 0, prior: l.amount });
        });
        Array.from(byKey.values()).forEach((l) => rows.push([label, l.accountNumber, `"${l.accountName}"`, fmtCSV(l.current), fmtCSV(l.prior)]));
        rows.push([label, '', `Total ${label}`, fmtCSV(curTotal), fmtCSV(priorTotal)]);
        rows.push([]);
      };
      section('Revenue', current.revenue.lines, prior.revenue.lines, current.revenue.total, prior.revenue.total);
      section('Cost of Services', current.costOfServices.lines, prior.costOfServices.lines, current.costOfServices.total, prior.costOfServices.total);
      rows.push(['', '', 'Gross Profit', fmtCSV(current.grossProfit), fmtCSV(prior.grossProfit)]);
      rows.push([]);
      section('Operating Expenses', current.operatingExpenses.lines, prior.operatingExpenses.lines, current.operatingExpenses.total, prior.operatingExpenses.total);
      rows.push(['', '', 'Operating Income', fmtCSV(current.operatingIncome), fmtCSV(prior.operatingIncome)]);
      rows.push([]);
      section('Other Income (Expenses)', current.otherIncomeExpense.lines, prior.otherIncomeExpense.lines, current.otherIncomeExpense.total, prior.otherIncomeExpense.total);
      rows.push(['', '', 'Net Income Before Tax', fmtCSV(current.netIncomeBeforeTax), fmtCSV(prior.netIncomeBeforeTax)]);
      rows.push([]);
      section('Provision for Income Tax', current.provisionForIncomeTax.lines, prior.provisionForIncomeTax.lines, current.provisionForIncomeTax.total, prior.provisionForIncomeTax.total);
      rows.push(['', '', current.netIncome >= 0 ? 'Net Income' : 'Net Loss', fmtCSV(current.netIncome), fmtCSV(prior.netIncome)]);
    } else {
      rows.push(['Section', 'Account Number', 'Account Name', `${period.startDate} to ${period.endDate}`]);
      const section = (label, lines, total) => {
        lines.forEach((l) => rows.push([label, l.accountNumber, `"${l.accountName}"`, fmtCSV(l.amount)]));
        rows.push([label, '', `Total ${label}`, fmtCSV(total)]);
        rows.push([]);
      };
      section('Revenue', current.revenue.lines, current.revenue.total);
      section('Cost of Services', current.costOfServices.lines, current.costOfServices.total);
      rows.push(['', '', 'Gross Profit', fmtCSV(current.grossProfit)]);
      rows.push([]);
      section('Operating Expenses', current.operatingExpenses.lines, current.operatingExpenses.total);
      rows.push(['', '', 'Operating Income', fmtCSV(current.operatingIncome)]);
      rows.push([]);
      section('Other Income (Expenses)', current.otherIncomeExpense.lines, current.otherIncomeExpense.total);
      rows.push(['', '', 'Net Income Before Tax', fmtCSV(current.netIncomeBeforeTax)]);
      rows.push([]);
      section('Provision for Income Tax', current.provisionForIncomeTax.lines, current.provisionForIncomeTax.total);
      rows.push(['', '', current.netIncome >= 0 ? 'Net Income' : 'Net Loss', fmtCSV(current.netIncome)]);
    }

    const csv = rows.map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `income-statement-${segment.toLowerCase()}-${period.startDate}-to-${period.endDate}.csv`;
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // ── Export: PDF (server-rendered via pdfkit) ──────────────────────────────
  const exportPDF = async () => {
    try {
      const blob = await incomeStatementAPI.exportPdf({
        startDate: period.startDate,
        endDate: period.endDate,
        accountingClientId,
        segment,
      });
      const pdfBlob = new Blob([blob], { type: 'application/pdf' });
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `income-statement-${segment.toLowerCase()}-${period.startDate}-to-${period.endDate}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      notify.error(typeof err === 'string' ? err : 'Failed to export income statement to PDF');
    }
  };

  // ── Print (browser print window, mirrors on-screen PFRS layout) ───────────
  const handlePrint = () => {
    if (!current) return;
    const segmentLabel = { ALL: 'Combined', STANDARD: 'Standard', TRUCKING: 'Trucking' }[segment] || 'Combined';
    const fmt = (n, showCurrency) => formatMoney(n, { showCurrency });

    const baseStyle = `<style>body{font-family:'Courier New',monospace;font-size:12px;color:#111;margin:40px}
.hdr{text-align:center;margin-bottom:28px}.hdr h1{font-size:18px;margin:0 0 2px}.hdr h2{font-size:13px;margin:0;font-weight:normal}
.sub{font-size:11px;color:#6b7280;margin-top:6px}table{width:100%;border-collapse:collapse}
.sec{font-size:10px;font-weight:bold;color:#6b7280;text-transform:uppercase;letter-spacing:.05em;padding:10px 0 4px}
.row td{padding:2px 0}.indent{padding-left:16px}
.single{border-top:1px solid #9ca3af}.double{border-top:3px double #111;border-bottom:3px double #111}
.tb{text-align:center;margin-bottom:20px}.tb button{padding:7px 18px;font-size:12px;cursor:pointer;background:#0d9488;color:#fff;border:none;border-radius:4px;font-family:'Courier New',monospace}
@media print{.tb{display:none}@page{margin:20mm}}</style>`;

    let firstShown = false;
    const cell = (amount) => {
      const show = !firstShown;
      firstShown = true;
      return fmt(amount, show);
    };

    const detailRows = (lines, indent) => lines.map((l) =>
      `<tr class="row"><td class="${indent ? 'indent' : ''}">${l.accountName}</td><td style="text-align:right;font-family:monospace">${cell(l.amount)}</td></tr>`
    ).join('');
    const totalRow = (label, amount, double) =>
      `<tr class="row ${double ? 'double' : 'single'}"><td style="font-weight:bold;padding-top:4px">${label}</td><td style="text-align:right;font-family:monospace;font-weight:bold;padding-top:4px">${cell(amount, true)}</td></tr>`;
    const sectionHeader = (label) => `<tr><td colspan="2" class="sec">${label}</td></tr>`;

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Income Statement — ${segmentLabel}</title>
${baseStyle}</head><body>
<div class="tb"><button onclick="window.print()">🖨 Print / Save as PDF</button></div>
<div class="hdr">${accountingClientName ? `<div style="font-size:16px;font-weight:bold;margin-bottom:2px;">${accountingClientName}</div>` : ''}
<h1>Income Statement — ${segmentLabel}</h1>
<div class="sub">For the period ${period.startDate} to ${period.endDate}</div>
<div class="sub">All amounts in Philippine peso (₱)</div></div>
<table>
${sectionHeader('Revenue')}${detailRows(current.revenue.lines)}${totalRow('Total revenue', current.revenue.total)}
${sectionHeader('Cost of services')}${detailRows(current.costOfServices.lines)}${totalRow('Total cost of services', current.costOfServices.total)}
${totalRow('Gross profit', current.grossProfit)}
${sectionHeader('Operating expenses')}${detailRows(current.operatingExpenses.lines, true)}${totalRow('Total operating expenses', current.operatingExpenses.total)}
${totalRow('Operating income', current.operatingIncome)}
${sectionHeader('Other income (expenses)')}${detailRows(current.otherIncomeExpense.lines)}${totalRow('Total other income (expenses)', current.otherIncomeExpense.total)}
${totalRow('Net income before tax', current.netIncomeBeforeTax)}
${sectionHeader('Provision for income tax')}${detailRows(current.provisionForIncomeTax.lines)}${totalRow('Total provision for income tax', current.provisionForIncomeTax.total)}
${totalRow(current.netIncome >= 0 ? 'Net income' : 'Net loss', current.netIncome, true)}
</table>
<div style="margin-top:24px;text-align:center;font-size:10px;color:#6b7280;">Printed on ${new Date().toLocaleString('en-PH')}</div>
</body></html>`;

    const win = window.open('', '_blank', 'width=800,height=1000');
    win.document.write(html);
    win.document.close();
    setTimeout(() => { win.focus(); win.print(); }, 500);
  };

  // ── Render helpers ─────────────────────────────────────────────────────────
  let firstAmountShown = false;
  const money = (amount, force = false) => {
    const showCurrency = force || !firstAmountShown;
    firstAmountShown = true;
    return formatMoney(amount, { showCurrency });
  };

  const DetailLine = ({ line, indent }) => {
    const id = String(line._id);
    const isOpen = expandedAccounts.has(id);
    const isLoading = loadingBreakdown.has(id);
    const entries = breakdownData[id];

    return (
      <React.Fragment>
        <div
          onClick={() => toggleBreakdown(id)}
          className={`grid grid-cols-12 py-1 border-b border-ink-line/40 cursor-pointer hover:bg-ink-canvas select-none ${indent ? 'pl-4' : ''}`}
        >
          <div className="col-span-9 text-ink flex items-center gap-2">
            <span className="text-ink-body text-[10px] inline-block transition-transform" style={{ transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
            <span className="text-ink-body text-xs">{line.accountNumber}</span>
            {line.accountName}
          </div>
          <div className="col-span-3 text-right font-mono tabular-nums">{money(line.amount)}</div>
        </div>
        {isOpen && (
          <div className="bg-ink-canvas border-b border-ink-line">
            {isLoading ? (
              <div className="pl-10 py-3 text-xs text-ink-body italic">Loading transactions…</div>
            ) : entries && entries.length > 0 ? (
              <div className="pl-10 pr-4 py-2 overflow-x-auto">
                <table className="w-full text-xs font-mono">
                  <thead>
                    <tr className="text-[10px] text-ink-body uppercase tracking-wide border-b border-ink-line">
                      <th className="text-left py-1 pr-3 font-medium">Date</th>
                      <th className="text-left py-1 pr-3 font-medium">Description</th>
                      <th className="text-left py-1 pr-3 font-medium">JE #</th>
                      <th className="text-right py-1 font-medium">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((e, i) => {
                      const je = e.journalEntry;
                      const jeNum = je?.entryNumber || '—';
                      const acctNormal = e.account?.normalBalance;
                      const amt = acctNormal === 'credit'
                        ? (e.credit || 0) - (e.debit || 0)
                        : (e.debit || 0) - (e.credit || 0);
                      const jeUrl = je?._id ? `/journal-entry/${je._id}/edit` : null;
                      return (
                        <tr key={e._id || i} className="border-b border-ink-line/40">
                          <td className="py-0.5 pr-3 whitespace-nowrap text-ink-body">{fmtTxnDate(e.transactionDate)}</td>
                          <td className="py-0.5 pr-3 text-ink max-w-xs truncate">{e.description || '—'}</td>
                          <td className="py-0.5 pr-3">
                            {jeUrl ? <Link to={jeUrl} target="_blank" rel="noopener noreferrer" className="text-primary-700 hover:underline font-medium">{jeNum}</Link> : <span className="text-ink-body">{jeNum}</span>}
                          </td>
                          <td className="py-0.5 text-right tabular-nums">{formatMoney(amt)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : entries ? (
              <div className="pl-10 py-3 text-xs text-ink-body italic">No transactions found for this account in the selected period.</div>
            ) : null}
          </div>
        )}
      </React.Fragment>
    );
  };

  const SectionHeading = ({ children }) => (
    <div className="mt-4 mb-1 text-xs font-medium text-ink-body uppercase tracking-widest">{children}</div>
  );

  const TotalRow = ({ label, amount, doubleRule = false }) => (
    <div className={`grid grid-cols-12 py-2 mt-1 font-medium ${doubleRule ? 'border-t-2 border-b-2 border-double border-ink text-base' : 'border-t border-ink-line'}`}>
      <div className="col-span-9">{label}</div>
      <div className="col-span-3 text-right font-mono tabular-nums">{money(amount, true)}</div>
    </div>
  );

  const ComparativeRows = ({ curLines, priorLines }) => {
    const byKey = new Map();
    curLines.forEach((l) => byKey.set(l.accountNumber, { ...l, current: l.amount, prior: 0 }));
    priorLines.forEach((l) => {
      const existing = byKey.get(l.accountNumber);
      if (existing) existing.prior = l.amount;
      else byKey.set(l.accountNumber, { ...l, current: 0, prior: l.amount });
    });
    const rows = Array.from(byKey.values()).sort((a, b) => String(a.accountNumber).localeCompare(String(b.accountNumber)));
    return rows.map((r) => (
      <div key={r.accountNumber} className="grid grid-cols-12 py-1 border-b border-ink-line/40">
        <div className="col-span-6 text-ink pl-4"><span className="mr-2 text-ink-body text-xs">{r.accountNumber}</span>{r.accountName}</div>
        <div className="col-span-3 text-right font-mono tabular-nums">{money(r.current)}</div>
        <div className="col-span-3 text-right font-mono tabular-nums">{money(r.prior)}</div>
      </div>
    ));
  };

  const TotalRowComparative = ({ label, current: cur, prior: pr, doubleRule = false }) => (
    <div className={`grid grid-cols-12 py-2 mt-1 font-medium ${doubleRule ? 'border-t-2 border-b-2 border-double border-ink text-base' : 'border-t border-ink-line'}`}>
      <div className="col-span-6">{label}</div>
      <div className="col-span-3 text-right font-mono tabular-nums">{money(cur, true)}</div>
      <div className="col-span-3 text-right font-mono tabular-nums">{money(pr, true)}</div>
    </div>
  );

  return (
    <div className="space-y-4">
      <ReportToolbar
        showSegment={isBilling}
        segment={segment}
        onSegmentChange={setSegment}
        period={period}
        onPeriodChange={setPeriod}
        fiscalYear={fiscalYear}
        showComparative
        comparative={comparative}
        onComparativeChange={setComparative}
        onExportCSV={current ? exportCSV : undefined}
        onExportPDF={current ? exportPDF : undefined}
        onPrint={current ? handlePrint : undefined}
        onRefresh={() => refetch()}
        refreshing={isFetching}
      />

      {isError && (
        <div className="rounded-md bg-danger-50 border border-danger-200 p-4 text-sm text-danger-700">
          {error?.message || 'Failed to fetch income statement'}
        </div>
      )}

      {isFetching && !current && (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500"></div>
        </div>
      )}

      {!accountingClientId && (
        <div className="rounded-md bg-ink-canvas border border-ink-line p-4 text-sm text-ink-body text-center">
          Select an accounting client to view its income statement.
        </div>
      )}

      {current && (
        <div className="bg-white border border-ink-line rounded-lg p-8 max-w-4xl mx-auto">
          <StatementHeader
            clientName={accountingClientName}
            segment={segment}
            startDate={period.startDate}
            endDate={period.endDate}
          />

          {comparative && prior ? (
            <>
              <div className="grid grid-cols-12 border-b-2 border-ink-line pb-2 mb-1 text-xs font-medium text-ink-body uppercase tracking-wide">
                <div className="col-span-6">Account</div>
                <div className="col-span-3 text-right">Current</div>
                <div className="col-span-3 text-right">Prior year</div>
              </div>

              <SectionHeading>Revenue</SectionHeading>
              <ComparativeRows curLines={current.revenue.lines} priorLines={prior.revenue.lines} />
              <TotalRowComparative label="Total revenue" current={current.revenue.total} prior={prior.revenue.total} />

              <SectionHeading>Cost of services</SectionHeading>
              <ComparativeRows curLines={current.costOfServices.lines} priorLines={prior.costOfServices.lines} />
              <TotalRowComparative label="Total cost of services" current={current.costOfServices.total} prior={prior.costOfServices.total} />
              <TotalRowComparative label="Gross profit" current={current.grossProfit} prior={prior.grossProfit} />

              <SectionHeading>Operating expenses</SectionHeading>
              <ComparativeRows curLines={current.operatingExpenses.lines} priorLines={prior.operatingExpenses.lines} />
              <TotalRowComparative label="Total operating expenses" current={current.operatingExpenses.total} prior={prior.operatingExpenses.total} />
              <TotalRowComparative label="Operating income" current={current.operatingIncome} prior={prior.operatingIncome} />

              <SectionHeading>Other income (expenses)</SectionHeading>
              <ComparativeRows curLines={current.otherIncomeExpense.lines} priorLines={prior.otherIncomeExpense.lines} />
              <TotalRowComparative label="Total other income (expenses)" current={current.otherIncomeExpense.total} prior={prior.otherIncomeExpense.total} />
              <TotalRowComparative label="Net income before tax" current={current.netIncomeBeforeTax} prior={prior.netIncomeBeforeTax} />

              <SectionHeading>Provision for income tax</SectionHeading>
              <ComparativeRows curLines={current.provisionForIncomeTax.lines} priorLines={prior.provisionForIncomeTax.lines} />
              <TotalRowComparative label="Total provision for income tax" current={current.provisionForIncomeTax.total} prior={prior.provisionForIncomeTax.total} />

              <TotalRowComparative
                label={current.netIncome >= 0 ? 'Net income' : 'Net loss'}
                current={current.netIncome}
                prior={prior.netIncome}
                doubleRule
              />
            </>
          ) : (
            <>
              <div className="grid grid-cols-12 border-b-2 border-ink-line pb-2 mb-1 text-xs font-medium text-ink-body uppercase tracking-wide">
                <div className="col-span-9">Account</div>
                <div className="col-span-3 text-right">Amount</div>
              </div>

              <SectionHeading>Revenue</SectionHeading>
              {current.revenue.lines.map((l) => <DetailLine key={l._id} line={l} />)}
              <TotalRow label="Total revenue" amount={current.revenue.total} />

              <SectionHeading>Cost of services</SectionHeading>
              {current.costOfServices.lines.map((l) => <DetailLine key={l._id} line={l} />)}
              <TotalRow label="Total cost of services" amount={current.costOfServices.total} />
              <TotalRow label="Gross profit" amount={current.grossProfit} />

              <SectionHeading>Operating expenses</SectionHeading>
              {current.operatingExpenses.lines.map((l) => <DetailLine key={l._id} line={l} indent />)}
              <TotalRow label="Total operating expenses" amount={current.operatingExpenses.total} />
              <TotalRow label="Operating income" amount={current.operatingIncome} />

              <SectionHeading>Other income (expenses)</SectionHeading>
              {current.otherIncomeExpense.lines.map((l) => <DetailLine key={l._id} line={l} />)}
              <TotalRow label="Total other income (expenses)" amount={current.otherIncomeExpense.total} />
              <TotalRow label="Net income before tax" amount={current.netIncomeBeforeTax} />

              <SectionHeading>Provision for income tax</SectionHeading>
              {current.provisionForIncomeTax.lines.map((l) => <DetailLine key={l._id} line={l} />)}
              <TotalRow label="Total provision for income tax" amount={current.provisionForIncomeTax.total} />

              <TotalRow
                label={current.netIncome >= 0 ? 'Net income' : 'Net loss'}
                amount={current.netIncome}
                doubleRule
              />
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default IncomeStatementComparative;
